import {
  getDarkSurfaceScrollbarSx,
  getLightSurfaceScrollbarSx,
} from '@app/core/ui/thin-scrollbar-sx';
import { CSSObject, styled, Theme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import {
  getSidenavBackgroundColor,
  getSidenavBorderColor,
  getSidenavTextColor,
  isSidenavLightSurface,
} from './sidenav-colors';
import { SIDENAV_TOKENS } from './sidenav-tokens';

const DRAWER_WIDTH = SIDENAV_TOKENS.drawerWidth;
const DRAWER_WIDTH_COLLAPSED = SIDENAV_TOKENS.collapsedWidth;

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH_COLLAPSED,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => {
  const lightSurface = isSidenavLightSurface(theme);
  const borderColor = getSidenavBorderColor(theme);

  return {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open ? openedMixin(theme) : closedMixin(theme)),
    '& .MuiDrawer-paper': {
      backgroundColor: getSidenavBackgroundColor(theme),
      color: getSidenavTextColor(theme),
      overflowY: 'auto',
      // Light: gray-200 edge + soft shadow so the rail reads as a control panel.
      borderRight: lightSurface ? `1px solid ${borderColor}` : 'none',
      boxShadow: lightSurface ? '2px 0 8px rgba(0, 0, 0, 0.04)' : 'none',
      ...(lightSurface ? getLightSurfaceScrollbarSx() : getDarkSurfaceScrollbarSx()),
      ...(open ? openedMixin(theme) : closedMixin(theme)),
      '& .visible-collapsed': {
        display: open ? 'none' : 'inline-block',
      },
      '& .hidden-collapsed': {
        display: open ? 'block' : 'none',
      },
    },
  };
});
